import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { Alert, App, Button as AntButton, Form, Image, Input, Popconfirm, Select, Skeleton, Space, Switch, Upload } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, FK, PageTitle, Panel } from '../components/ds';
import { ApiError, friendlyError } from '../lib/api';
import {
  DOSSIER_LIMITS,
  DOSSIER_PHOTO_MAX_MB,
  MONTHS_RU,
  daysInBirthdayMonth,
  dossierApi,
  type DossierBirthday,
  type MyDossier,
  type MyDossierPatch,
} from '../lib/api/dossier';

/**
 * «Моё досье» (/dossier, обращение FB-2, 31.10.2026): партнёр сам рассказывает о себе — день рождения (только день и месяц),
 * хобби и увлечения, «10 фактов о себе», семья, достижения, до 6 фото. Тексты и день рождения сохраняются кнопкой,
 * фото — сразу при загрузке. Хобби, факты, день рождения и фото видят другие партнёры и команда УК в «Партнёры сети»
 * (если включено «Показывать другим партнёрам»); семью и достижения — только сам партнёр и команда УК.
 * Телефон, почта, ИНН и деньги в досье не попадают.
 */

const QUERY_KEY = ['wiki', 'dossier', 'mine'] as const;

interface FormVals {
  birthdayDay: number | null;
  birthdayMonth: number | null;
  hobbies: string;
  about: string;
  family: string;
  achievements: string;
  showToPartners: boolean;
}

const toVals = (d: MyDossier): FormVals => ({
  birthdayDay: d.birthday?.day ?? null,
  birthdayMonth: d.birthday?.month ?? null,
  hobbies: d.hobbies,
  about: d.about,
  family: d.family,
  achievements: d.achievements,
  showToPartners: d.showToPartners,
});

function birthdayOf(v: Pick<FormVals, 'birthdayDay' | 'birthdayMonth'>): DossierBirthday | null {
  return v.birthdayDay && v.birthdayMonth ? { day: v.birthdayDay, month: v.birthdayMonth } : null;
}

export function MyDossierPage(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<FormVals>();
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => dossierApi.mine(), retry: false });

  const save = useMutation({
    mutationFn: (patch: MyDossierPatch) => dossierApi.save(patch),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      void qc.invalidateQueries({ queryKey: ['wiki', 'dossier', 'network'] });
      message.success('Досье сохранено');
    },
    onError: (e: unknown) => message.error(friendlyError(e)),
  });

  const onFinish = (v: FormVals) => {
    const birthday = birthdayOf(v);
    if ((v.birthdayDay || v.birthdayMonth) && !birthday) {
      message.error('День рождения: выберите и день, и месяц — или очистите оба');
      return;
    }
    if (birthday && birthday.day > daysInBirthdayMonth(birthday.month)) {
      message.error('День рождения: в этом месяце нет такого числа');
      return;
    }
    save.mutate({
      birthday,
      hobbies: v.hobbies ?? '',
      about: v.about ?? '',
      family: v.family ?? '',
      achievements: v.achievements ?? '',
      showToPartners: !!v.showToPartners,
    });
  };

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 10 }} />;
  if (q.isError || !q.data) {
    const forbidden = q.error instanceof ApiError && q.error.status === 403;
    return (
      <div>
        <PageTitle title="Моё досье" />
        <Alert
          type={forbidden ? 'info' : 'error'}
          showIcon
          message={forbidden ? 'Досье заполняют партнёры' : 'Не удалось загрузить досье'}
          description={forbidden ? 'Этот раздел доступен вошедшим партнёрам. Чужие досье можно читать в «Партнёры сети».' : (q.error as Error | null)?.message}
        />
      </div>
    );
  }
  const d = q.data;

  return (
    <div style={{ maxWidth: 860 }}>
      <PageTitle
        title="Моё досье"
        sub="Расскажите коллегам по сети о себе: чем увлекаетесь, чем гордитесь и как вас поздравлять"
        right={
          <Button variant="ghost" to="/partners">
            Партнёры сети
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Что увидят другие"
        description="Хобби, факты о себе, день рождения (только день и месяц, без года) и фото увидят партнёры сети и команда УК. Семью и достижения видите только вы и команда УК. Телефон, почту, ИНН и деньги в досье никто не увидит."
      />

      {/* Значения формы берутся один раз при показе: загрузка фото обновляет кэш досье, но не затирает то, что человек печатает. */}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish} initialValues={toVals(d)}>
        <Panel title="О себе" style={{ marginBottom: 16 }}>
          <Form.Item label="День рождения (год не нужен)" style={{ marginBottom: 12 }}>
            <Space.Compact>
              <Form.Item name="birthdayDay" noStyle>
                <Select allowClear placeholder="День" style={{ width: 90 }} options={Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))} />
              </Form.Item>
              <Form.Item name="birthdayMonth" noStyle>
                <Select allowClear placeholder="Месяц" style={{ width: 160 }} options={MONTHS_RU.map((m, i) => ({ value: i + 1, label: m }))} />
              </Form.Item>
            </Space.Compact>
            <Form.Item noStyle shouldUpdate={(a: FormVals, c: FormVals) => a.birthdayDay !== c.birthdayDay || a.birthdayMonth !== c.birthdayMonth}>
              {({ getFieldValue }) => {
                const day = getFieldValue('birthdayDay') as number | null;
                const month = getFieldValue('birthdayMonth') as number | null;
                if (day && month && day > daysInBirthdayMonth(month)) {
                  return <div style={{ color: FK.error, fontSize: 12, marginTop: 4 }}>В этом месяце нет такого числа</div>;
                }
                if (d.cardBirthday && !day && !month) {
                  const c = d.cardBirthday;
                  return (
                    <AntButton type="link" size="small" style={{ padding: 0, marginTop: 4 }} onClick={() => form.setFieldsValue({ birthdayDay: c.day, birthdayMonth: c.month })}>
                      Подставить из карточки: {c.day} {MONTHS_RU[c.month - 1]}
                    </AntButton>
                  );
                }
                return null;
              }}
            </Form.Item>
          </Form.Item>

          <Form.Item label="Хобби и увлечения" name="hobbies">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} showCount maxLength={DOSSIER_LIMITS.hobbies} placeholder="Рыбалка, настолки, бег по утрам, коллекционирую винил…" />
          </Form.Item>
          <Form.Item
            label="10 фактов о себе"
            name="about"
            extra="Хобби, увлечения, достижения, семейное положение, смешные факапы в жизни — всё, что поможет коллегам вас узнать."
          >
            <Input.TextArea
              autoSize={{ minRows: 6, maxRows: 24 }}
              showCount
              maxLength={DOSSIER_LIMITS.about}
              placeholder={'1. Родился в …\n2. Открыл первую точку в …\n3. …'}
            />
          </Form.Item>
        </Panel>

        <Panel title="Только для вас и команды УК" sub="Эти два поля другим партнёрам не показываются" style={{ marginBottom: 16 }}>
          <Form.Item label="Семья" name="family">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} showCount maxLength={DOSSIER_LIMITS.family} />
          </Form.Item>
          <Form.Item label="Достижения" name="achievements" style={{ marginBottom: 0 }}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} showCount maxLength={DOSSIER_LIMITS.achievements} />
          </Form.Item>
        </Panel>

        <Panel title="Показ другим партнёрам" style={{ marginBottom: 16 }}>
          <Form.Item
            name="showToPartners"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            extra="Выключите, и ваша карточка исчезнет из раздела «Партнёры сети». Команда УК по-прежнему видит досье в CRM."
          >
            <Switch checkedChildren="Показывать" unCheckedChildren="Скрыто" />
          </Form.Item>
        </Panel>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить досье
          </Button>
          {d.exists && !d.showToPartners ? <span style={{ fontSize: 13, color: FK.text2 }}>Сейчас досье скрыто от других партнёров</span> : null}
        </div>
      </Form>

      <PhotosPanel dossier={d} />
    </div>
  );
}

/** Фото: до 6 штук, грузятся сразу и по одному (очередь), «Удалить» с подтверждением. */
function PhotosPanel({ dossier }: { dossier: MyDossier }): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [caption, setCaption] = useState('');
  const [pending, setPending] = useState(0);
  const [removing, setRemoving] = useState<string | null>(null);
  // Выбранные несколько файлов грузим друг за другом: параллельные запросы упёрлись бы в блокировку строки досье.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const left = DOSSIER_LIMITS.photos - dossier.photos.length - pending;

  const pick = (file: File): false => {
    if (left <= 0) {
      message.error(`Можно не больше ${DOSSIER_LIMITS.photos} фото — удалите лишнее`);
      return false;
    }
    if (file.size > DOSSIER_PHOTO_MAX_MB * 1024 * 1024) {
      message.error(`Фото «${file.name}» больше ${DOSSIER_PHOTO_MAX_MB} МБ`);
      return false;
    }
    const cap = caption.trim().slice(0, DOSSIER_LIMITS.caption);
    setPending((n) => n + 1);
    queue.current = queue.current.then(async () => {
      try {
        qc.setQueryData(QUERY_KEY, await dossierApi.addPhoto(file, cap));
        void qc.invalidateQueries({ queryKey: ['wiki', 'dossier', 'network'] });
      } catch (e) {
        message.error(friendlyError(e));
      } finally {
        setPending((n) => n - 1);
      }
    });
    setCaption('');
    return false;
  };

  const remove = async (photoId: string): Promise<void> => {
    setRemoving(photoId);
    try {
      qc.setQueryData(QUERY_KEY, await dossierApi.removePhoto(photoId));
      void qc.invalidateQueries({ queryKey: ['wiki', 'dossier', 'network'] });
    } catch (e) {
      message.error(friendlyError(e));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Panel title={`Фото (${dossier.photos.length} из ${DOSSIER_LIMITS.photos})`} sub={`JPG, PNG или WebP до ${DOSSIER_PHOTO_MAX_MB} МБ — они появятся в вашей карточке сразу`}>
      <Image.PreviewGroup>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', gap: 12 }}>
          {dossier.photos.map((p) => (
            <div key={p.id} style={{ position: 'relative', minWidth: 0 }}>
              <Image
                src={dossierApi.photoUrl(dossier.partnerId, p.id, 'thumb')}
                alt={p.caption ?? 'Моё фото'}
                width="100%"
                height={130}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                preview={{ src: dossierApi.photoUrl(dossier.partnerId, p.id, 'full') }}
              />
              {p.caption ? <div style={{ fontSize: 12, color: FK.text2, marginTop: 2 }}>{p.caption}</div> : null}
              <Popconfirm title="Удалить фото?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove(p.id)}>
                <AntButton size="small" danger icon={<DeleteOutlined />} loading={removing === p.id} aria-label="Удалить фото" style={{ position: 'absolute', top: 6, right: 6 }} />
              </Popconfirm>
            </div>
          ))}
          {Array.from({ length: pending }, (_, i) => (
            <div key={`pending-${i}`} style={{ height: 130, borderRadius: 6, background: FK.bgSubtle, display: 'grid', placeItems: 'center', color: FK.muted, fontSize: 13 }}>
              Загружается…
            </div>
          ))}
        </div>
      </Image.PreviewGroup>

      {left > 0 ? (
        <div style={{ marginTop: dossier.photos.length > 0 || pending > 0 ? 16 : 0, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={DOSSIER_LIMITS.caption}
            placeholder="Подпись к следующему фото (необязательно)"
            style={{ maxWidth: 360 }}
          />
          <Upload accept="image/jpeg,image/png,image/webp" multiple showUploadList={false} beforeUpload={(file) => pick(file as unknown as File)}>
            <AntButton icon={<PlusOutlined />}>Добавить фото</AntButton>
          </Upload>
        </div>
      ) : (
        <div style={{ marginTop: 12, fontSize: 13, color: FK.text2 }}>Загружено максимум фото. Чтобы добавить новое, удалите одно из старых.</div>
      )}
    </Panel>
  );
}
